const AUTH_TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const POST_LOGIN_FROM_KEY = 'auth_redirect_from';

/** Minutes before expiry to show session warning */
export const SESSION_WARNING_MINUTES = 5;

/**
 * Check if a JWT token is expired.
 * @param {string|null} token - JWT token string
 * @returns {boolean} true if expired or invalid
 */
export function isTokenExpired(token) {
    if (!token) return true;
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.exp * 1000 < Date.now();
    } catch {
        return true;
    }
}

/**
 * Check if token is about to expire (within SESSION_WARNING_MINUTES).
 * @param {string|null} token - JWT token string
 * @returns {boolean} true if expiring soon
 */
export function isTokenExpiringSoon(token) {
    if (!token) return false;
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expMs = payload.exp * 1000;
        const warningThreshold = Date.now() + SESSION_WARNING_MINUTES * 60 * 1000;
        return expMs > Date.now() && expMs < warningThreshold;
    } catch {
        return false;
    }
}

/**
 * Get stored token without expiry check (for refresh attempt).
 * @returns {string|null} Raw token from storage
 */
export function getStoredToken() {
    return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function getAuthToken() {
    const token = getStoredToken();
    return isTokenExpired(token) ? null : token;
}

export function setAuthToken(token) {
    if (token) {
        localStorage.setItem(AUTH_TOKEN_KEY, token);
    } else {
        localStorage.removeItem(AUTH_TOKEN_KEY);
    }
}

export function getRefreshToken() {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setRefreshToken(token) {
    if (token) {
        localStorage.setItem(REFRESH_TOKEN_KEY, token);
    } else {
        localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
}

export function persistAuthTokens({ accessToken, refreshToken } = {}) {
    if (accessToken) setAuthToken(accessToken);
    if (refreshToken) setRefreshToken(refreshToken);
}

export function clearAuthTokens() {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export function clearAuthToken() {
    localStorage.removeItem(AUTH_TOKEN_KEY);
}

/**
 * Same-origin relative path starting with `/` (not protocol-relative).
 */
export function isSafeInternalPath(path) {
    return typeof path === 'string'
        && path.startsWith('/')
        && !path.startsWith('//');
}

/**
 * Persist ProtectedRoute `from` across Threedium OAuth (which leaves the app).
 */
export function persistLoginFrom(from) {
    if (!from) return;
    const path = typeof from === 'string'
        ? from
        : `${from.pathname || ''}${from.search || ''}${from.hash || ''}`;
    if (!isSafeInternalPath(path) || path === '/login' || path.startsWith('/auth/')) return;
    sessionStorage.setItem(POST_LOGIN_FROM_KEY, path);
}

/**
 * Consume the post-login redirect, or `/dashboard` if missing/unsafe.
 */
export function consumeLoginFrom() {
    const path = sessionStorage.getItem(POST_LOGIN_FROM_KEY);
    sessionStorage.removeItem(POST_LOGIN_FROM_KEY);
    return isSafeInternalPath(path) ? path : '/dashboard';
}
